import * as React from "react";

export type PromoteVersionDialogProps = {
  open: boolean;
  revisionLabel: string;
  nextMajorVersion: number;
  filename: string;
  onFilenameChange: (value: string) => void;
  pending?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

export const PromoteVersionDialog: React.FC<PromoteVersionDialogProps> = ({
  open,
  revisionLabel,
  nextMajorVersion,
  filename,
  onFilenameChange,
  pending,
  onCancel,
  onConfirm,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const canPromote = filename.trim().length > 0;
  const revLabel = revisionLabel.trim() || "revision";

  React.useEffect(() => {
    if (!open) return undefined;
    const id = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(id);
  }, [open]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !pending) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, pending, onCancel]);

  if (!open) return null;

  return (
    <div
      className="versions-promote-modal-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !pending) onCancel();
      }}
    >
      <div
        className="versions-promote-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Promote ${revLabel}`}
      >
        <div className="versions-promote-modal__header">
          <div className="versions-promote-modal__title">
            Promote {revLabel} to v{nextMajorVersion}?
          </div>
          <p className="versions-promote-modal__desc">
            This creates a new major version for sending to the counterparty. Other active
            revisions under the same version will be marked superseded.
          </p>
        </div>
        <div className="versions-promote-modal__body">
          <ul className="versions-promote-modal__bullets">
            <li>{revLabel} becomes v{nextMajorVersion} (the new current major version)</li>
            <li>Other active revisions under this version will be marked superseded</li>
            <li>You can then send v{nextMajorVersion} for review</li>
          </ul>
          <label className="versions-promote-modal__label" htmlFor="promote-filename">
            File name
          </label>
          <input
            id="promote-filename"
            ref={inputRef}
            className="versions-promote-modal__input"
            value={filename}
            disabled={pending}
            placeholder={`v${nextMajorVersion}_Mutual_NDA_Template.docx`}
            onChange={(e) => onFilenameChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canPromote && !pending) onConfirm();
            }}
          />
        </div>
        <div className="versions-promote-modal__footer">
          <button
            type="button"
            className="versions-promote-modal__btn versions-promote-modal__btn--ghost"
            disabled={pending}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="versions-promote-modal__btn versions-promote-modal__btn--primary"
            disabled={pending || !canPromote}
            onClick={onConfirm}
          >
            {pending ? "Promoting…" : `Promote to v${nextMajorVersion}`}
          </button>
        </div>
      </div>
    </div>
  );
};
